import React, { useState, useEffect } from 'react';
import { useAuth } from '../../AuthContext';
import { leaderboardAPI } from '../../api';
import { cn } from '../../utils';
import { Card, Select, Spinner, EmptyState } from '../../components/ui';
import { Trophy } from 'lucide-react';

export const LeaderboardPage = () => {
  const { user } = useAuth();
  const [leaderboard, setLeaderboard] = useState(null);
  const [period, setPeriod] = useState('all_time');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadLeaderboard = async () => {
      setLoading(true);
      try {
        const response = await leaderboardAPI.get(period, 20);
        setLeaderboard(response.data);
      } catch (err) {
        console.error('Failed to load leaderboard:', err);
      } finally {
        setLoading(false);
      }
    };
    loadLeaderboard();
  }, [period]);

  return (
    <div className="space-y-6" data-testid="leaderboard-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Leaderboard</h1>
          <p className="text-gray-500">See how you rank against others</p>
        </div>
        <Select
          value={period}
          onChange={e => setPeriod(e.target.value)}
          options={[
            { value: 'weekly', label: 'This Week' },
            { value: 'monthly', label: 'This Month' },
            { value: 'all_time', label: 'All Time' },
          ]}
          className="w-36"
        />
      </div>

      {/* Current User Rank */}
      {leaderboard?.current_user_rank && (
        <Card className="p-4 bg-primary-50 border-primary-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-primary-700">Your Rank</p>
              <p className="text-3xl font-bold text-primary-900">#{leaderboard.current_user_rank}</p>
            </div>
            <p className="text-sm text-primary-600">
              out of {leaderboard.total_participants} participants
            </p>
          </div>
        </Card>
      )}

      {/* Leaderboard Table */}
      <Card>
        {loading ? (
          <div className="p-8 text-center"><Spinner /></div>
        ) : !leaderboard?.entries?.length ? (
          <EmptyState
            icon={Trophy}
            title="No rankings yet"
            description="Complete activities to appear on the leaderboard"
          />
        ) : (
          <div className="divide-y">
            {leaderboard.entries.map((entry, idx) => (
              <div 
                key={entry.user_id} 
                className={cn(
                  'p-4 flex items-center gap-4',
                  entry.is_current_user && 'bg-primary-50'
                )}
              >
                <div className={cn(
                  'w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold',
                  entry.rank === 1 ? 'bg-yellow-100 text-yellow-700' :
                  entry.rank === 2 ? 'bg-gray-200 text-gray-700' :
                  entry.rank === 3 ? 'bg-amber-100 text-amber-700' :
                  'bg-gray-100 text-gray-500'
                )}>
                  {entry.rank <= 3 ? ['🥇', '🥈', '🥉'][entry.rank - 1] : entry.rank}
                </div>
                <div className="flex-1">
                  <p className="font-medium">
                    {entry.display_name}
                    {entry.is_current_user && <span className="text-primary-600 ml-2">(You)</span>}
                  </p>
                  <p className="text-sm text-gray-500">{entry.level_name}</p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-primary-600">{entry.total_points?.toLocaleString() || 0}</p>
                  <p className="text-xs text-gray-500">points</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

export default LeaderboardPage;
